<template>
  <div class="input-datepicker" :class="{ difference, weird: weirdDate  }">
    <label v-if="label || forReport || forState">
      <span v-if="label" v-html="label" />
      <Icon v-if="forReport" name="fieldsSAV" />
      <Icon v-if="forState" name="fieldsState" />
      <Tooltip v-if="tooltip" icon="info" :content="tooltip" />
    </label>
    <VueDatePicker
      first-day-of-week="mon"
      :full-month-name="true"
      :calendar-button="true"
      :show-calendar-on-button-click="true"
      format="dd.MM.yyyy"
      placeholder="TT.MM.JJJJ"
      :language="locale.de"
      :disabled-dates="disabledDates"
      :typeable="true"
      v-model="ivalue"
    >
      <template #calendarBtn class="test">
        <Icon name="calendar" />
      </template>
    </VueDatePicker>
    <span v-if="hint" class="hint" v-html="hint" />
    <template v-if="difference">
      <button class="adopt" @click.stop.prevent="handleAdopt(true)">{{ $t('review.diff.adopt') }}</button>
      <button class="adopt" @click.stop.prevent="handleAdopt(false)">{{ $t('review.diff.ignore') }}</button>
    </template>
  </div>
</template>

<script>
  import VueDatePicker from '@sum.cumo/vue-datepicker';
  import { de } from '@sum.cumo/vue-datepicker/dist/locale/index.esm';
  import Icon from '@/components/ui/Icon';
  import Tooltip from '@/components/ui/Tooltip';
  import Toast from '@/components/ui/Toast';

  export default {
    name: 'Datepicker',
    components: {
      Tooltip,
      Icon,
      VueDatePicker,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      value: {
        type: String,
        default: null,
      },
      label: {
        type: String,
        default: null,
      },
      hint: {
        type: String,
        default: null,
      },
      tooltip: {
        type: String,
        default: null,
      },
      forState: {
        type: Boolean,
        default: false,
      },
      forReport: {
        type: Boolean,
        default: false,
      },
      minusYears: {
        type: Number,
        default: 0,
      },
      vuelidate: {
        type: Object,
        default: null,
      },
      //----------------------------------
      // CSS Related Properties
      //----------------------------------
      difference: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        ivalue: this.value || null,
        format: 'dd.MM.yyyy',
        locale: { de },
        weirdDate: false,
        disabledDates: null,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {},
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      value(val) {
        this.ivalue = val ? new Date(val) : null;
      },
      ivalue(val) {
        let value = null;
        this.weirdDate = false;
        if (val) {
          const day = `${val.getDate()}`.padStart(2, '0');
          const month = `${val.getMonth() + 1}`.padStart(2, '0');
          const year = val.getFullYear();
          value = `${year}-${month}-${day}`;

          const twentyYearsAgo = new Date().getFullYear() - 20;
          if (year < twentyYearsAgo) {
            this.weirdDate = true;
          }


          if (this.isOlderThanMinusYears(val)) {
            Toast.warn(`Datum is zu neu und muss mindestens<br/> ${this.minusYears} Jahre in der Vergangenheit liegen.`);
            this.weirdDate = true;
            value = null;
          }
        }
        this.$emit('update:value', value);
        this.$emit('input', value);
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      const rightNow = new Date();
      const minusFrom = new Date(rightNow);
      const hasMinusYears = this.minusYears > 0;
      minusFrom.setFullYear(rightNow.getFullYear() - this.minusYears);

      if (hasMinusYears) {
        this.disabledDates = {
          from: minusFrom,
        };
      }
      /*
      if (!this.value) {
        if (hasMinusYears) {
          this.ivalue = minusFrom;
        }
        this.$emit('update:value', this.ivalue);
        this.$emit('input', this.ivalue);
      }
      */
    },
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // Event Handlers
      //----------------------------------
      isOlderThanMinusYears(date) {
        if (this.minusYears > 0) {
          const rightNow = new Date();
          const minusFrom = new Date(rightNow);
          minusFrom.setFullYear(rightNow.getFullYear() - this.minusYears);
          return date > minusFrom;
        }
        return false;
      },
      handleAdopt(accept) {
        this.$emit('adopt', { accept, value: this.ivalue });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';
@import '~@sum.cumo/vue-datepicker/src/styles/style';

.input-datepicker {
  & > label {
    display: block;
    @include font-body-medium;
    color: $color-black-solid-100;
    margin-bottom: 4px;

    & > svg {
      position: relative;
      top: 2px;
      width: 14px;
      height: 14px;
      margin-left: 4px;
    }

    & > .comp-tooltip {
      margin-left: 8px;
    }
  }

  & > span.hint {
    margin-top: 4px;
    @include font-body-small;
    color: $color-black-alpha-60;
  }

  & > button.adopt {
    display: inline-block;
    outline: 0;
    border: 0;
    cursor: pointer;
    background: transparent;
    text-decoration: underline;
    margin: 8px 0 0;
    @include font-body-small;
    color: $color-black-alpha-60;
  }

  & > .vdp-datepicker {
    & > div:first-of-type {
      position: relative;
      .vdp-datepicker__calendar-button {
        position: absolute;
        top: calc(50% - 1px);
        right: 0;
        transform: translate(-13px, -50%);
        width: 24px;
        height: 24px;
        user-select: none;
      }

      input {
        width: 100%;
        padding: 13px 11px;
        height: 100%;
        border-radius: $default-border-radius;
        border: 1px solid $color-black-alpha-40;
        outline: 0;
        @include font-body-medium;
        color: $color-black-solid-100;
        transition: border $default-transition;

        &::placeholder {
          @include font-body-medium;
          color: $color-black-alpha-60;
        }

        &:focus {
          border: 1px solid $color-blue-alpha-80;
        }

        &.invalid {
          border: 1px solid red;
        }
      }
    }

    & > .vdp-datepicker__calendar {
      border-radius: $default-border-radius;
      overflow: hidden;
      border: 0;
      box-shadow: $box-shadow-5;
      background: $color-white-solid-10;

      .picker-view {
        padding: 4px;
        header {
          display: flex;
          align-items: center;
          height: 46px;
          background-color: transparent;

          .day__month_btn, .month__year_btn {
            @include font-body-medium;
            color: $color-black-solid-100;
            background: transparent;
            &:hover { background: transparent; }
          }

          .prev > span:after, .next > span:after {
            left: 15px;
            top: 50%;
            width: 13px;
            height: 13px;
            border: 2px solid $color-black-solid-100;
            transform-origin: 50% 50%;
            transform: translate(0, -6px) rotate(-45deg);
            border-radius: 2px;
            background: transparent;
          }

          .prev:hover, .next:hover {
            background-color: transparent;
          }

          .prev > span::after {
            border-right: 0;
            border-bottom: 0;
          }
          .next > span::after {
            left: 5px;
            border-left: 0;
            border-top: 0;
          }
        }

        .day-header {
          padding: 0 8px;
          display: inline-flex;
          justify-content: center;
          align-items: center;
          gap: 18px;
          @include font-body-small-bold;
        }

        .cell.month, .cell.year {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          @include font-body-small;
          color: rgba(0, 0, 0, 0.87);
          border-radius: $default-border-radius;

          &:hover, &.selected {
            background: transparent;
            border-color: $color-blue-alpha-80;
          }
        }

        .cell.day {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          position: relative;
          background: transparent;
          z-index: 2;
          @include font-body-small;
          color: rgba(0, 0, 0, 0.87);

          &:hover {
            border: 0;
            &:before {
              content: '';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 40px;
              height: 40px;
              border-radius: 100%;
              border: 1px solid $color-blue-alpha-80;
            }
          }

          &.disabled {
            opacity: 0.5;
          }

          &.selected {
            border: 0;
            color: $color-white-solid-10;
            &:before {
              content: '';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 40px;
              height: 40px;
              border-radius: 100%;
              background: $color-blue-alpha-80;
              z-index: -1;
            }
          }
        }

        .cell.month.disabled {
          opacity: 0.5;
        }
      }
    }
  }

  &.difference {
    & > .vdp-datepicker {
      & > div:first-of-type {
        input {
          box-shadow: 0 0 5px 3px rgba(214, 16, 16, 0.8);
        }
      }
    }
  }

  &.weird {
    & > .vdp-datepicker {
      & > div:first-of-type {
        input {
          border-color: $color-signal-orange;
        }
      }
    }
  }
}
</style>
